import { atomEffect } from 'jotai-effect';
import type { Atom } from 'jotai';
import localforage from 'localforage';

const isPromise = <T>(value: unknown): value is Promise<T> =>
  typeof value === 'object' && value !== null && 'then' in value;

/**
 * Creates an effect atom that persists changes to IndexedDB via localforage
 * Usage:
 *   const myAtom = atom({ name: 'test' });
 *   const effectAtom = localForageEffect(myAtom, 'myStorageKey');
 *
 *   // Mount the effect in a component to enable persistence:
 *   useAtom(effectAtom);
 *
 * For initialization from storage, use atomWithStorage from jotai/utils instead:
 *   import { atomWithStorage } from 'jotai/utils';
 */
export const localForageEffect = <T>(
  sourceAtom: Atom<T | Promise<T>>,
  key: string,
  debounceMs = 0
) =>
  atomEffect((get) => {
    const value = get(sourceAtom);

    const persist = (resolved: T) => {
      if (resolved === undefined || resolved === null || typeof resolved === 'symbol') {
        localforage.removeItem(key);
        return;
      }
      try {
        localforage.setItem(key, resolved);
      } catch (e) {
        // e.g. DataCloneError when value is not cloneable (e.g. RESET symbol passed through)
        localforage.removeItem(key);
      }
    };

    if (isPromise<T>(value)) {
      let cancelled = false;
      value.then((resolved) => {
        if (!cancelled) persist(resolved);
      });
      return () => {
        cancelled = true;
      };
    }

    if (debounceMs > 0) {
      const timer = setTimeout(() => persist(value), debounceMs);
      return () => clearTimeout(timer);
    }

    persist(value);
    return undefined;
  });
